import { Organization } from './models/organization.model';

export interface ServerError {
  message: string;
  property?: string;
}

export enum UserRoles {
  Admin = 'admin',
  Specialist = 'specialist',
  Patient = 'patient',
}

export enum UserActivity {
  Appointment = 'appointment',
  Lectures = 'lesson',
  ExerciseStrength = 'exercise-strength',
  ExercisePulse = 'exercise-pulse',
  Questionary = 'questionary',
}

export enum CalendarEntry {
  Appointment = 'appointment',
  Program = 'program',
}

export enum UserPermissions {
  All = 'all',
  LogOut = 'log-out',
  RefreshToken = 'refresh-token',
  ViewProfile = 'view-profile',
  EditProfile = 'edit-profile',
  EditSettings = 'edit-settings',
  ViewQuestionary = 'view-questionary',
  AnswerQuestionary = 'answer-questionary',
  ViewAnsweredQuestionary = 'view-answered-questionary',
  ViewSystemActivities = 'view-system-activities',
  ViewSystemPrograms = 'view-system-programs',
  ViewAssignedPrograms = 'view-assigned-programs',
}

export enum UserGender {
  Other = 0,
  Male = 1,
  Female = 2,
}

export enum CalendarFeedbackDifficulty {
  Easy = 'easy',
  Moderate = 'moderate',
  Hard = 'hard',
  NotSuitable = 'not-suitable',
}

export enum CalendarActivityState {
  Planned = 'planned',
  Done = 'done',
  Skipped = 'skipped',
}

export interface UserSession {
  id: string;
  email: string;
  role_id: number;
  role_type: UserRoles;
  role_name: string;
  permissions: UserPermissions[];
}

export interface TokenRequest {
  email: string;
  code?: string;
  otp?: string;
}

export interface TokenResponse {
  access_token: string;
}

export interface GrandidResponse {
  sessionId: string;
  redirectUrl: string;
}

export interface IDDto {
  id: number;
  created: number;
  updated: number;
}

export interface UUIDDto {
  id: string;
  created: number;
  updated: number;
}

export interface UserRoleEbd {
  id: number;
  name: string;
}

export interface UserProfileDto {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: number;
  gender: UserGender;
  phone: string;
  experience: string;
  about: string;
  jobTitle: string;
  photo: string;
  goal: string;
  physicalLimitationText?: string;
}

export interface UserDto extends UUIDDto {
  email: string;
  bankId: string;
  role: UserRoleEbd;
  profile?: UserProfileDto;
  fullName: string;
}

export interface SpecialistSettingsEbd {
  allowNotifications: boolean;
}

export interface SpecialistDto extends UserDto {
  settings: SpecialistSettingsEbd;
}

export interface PatientSettingsEbd {
  allowNotifications: boolean;
}

export interface PatientDto extends UserDto {
  settings: PatientSettingsEbd;
  organization: Organization | null;
  isActive: boolean;
  deletedAt?: string | null;
  lastActivityAt: number | null;
  isActiveLastMonth: boolean;
}

export type VideoCallAppointmentStatus = 'scheduled' | 'cancelled';

export interface VideoCallAppointmentDto extends UUIDDto {
  patientId: string;
  physioId: string;
  roomName: string;
  scheduledAt: number;
  note: string | null;
  status: VideoCallAppointmentStatus;
  lastRemindedAt: number | null;
  durationMinutes: number;
  patient?: { id: string; fullName: string; photo: string | null };
}

export interface PatientOverviewDto {
  summary: {
    exercisedToday: boolean;
    completedExercises: number;
    completedTasks: number;
    lastActivityAt: number | null;
  };
  recentActivities: PatientOverviewActivityDto[];
  /** Latest feedback submissions, not limited to the requested week. */
  recentFeedback: PatientOverviewFeedbackDto[];
  hasMoreFeedback: boolean;
}

export interface PatientOverviewActivityDto {
  id: string;
  kind: 'exercise' | 'task';
  /** Machine slug: program category / exercise type, or task type. */
  label: string;
  /** Real exercise name for exercise rows when known. */
  name: string | null;
  status: 'completed' | 'skipped' | string;
  occurredAt: number;
}

export interface PatientOverviewFeedbackDto {
  id: string;
  /** easy | moderate | hard | not-suitable | none */
  feedback: string | null;
  feedbackNote: string | null;
  exerciseType: string | null;
  /** Program category (ai/physical/sitting) or static exercise type. */
  category: string | null;
  exerciseName: string | null;
  /** Every exercise rated in this one submission. */
  exerciseNames: string[];
  created: number;
}

export interface VideoCallJoinDto {
  joinUrl: string;
}

export interface CalendarFeedbackEbd {
  difficulty: CalendarFeedbackDifficulty;
  description: string;
}

export interface ActivityDto extends IDDto {
  type: UserActivity;
  name: string;
  description: string;
  image: string;
  details: any;
}

export interface ProgramActivityDto extends IDDto {
  day: number;
  set: string;
  level: number;
  activity: ActivityDto;
  program: ProgramDto;
}

export interface CalendarEntryDto extends UUIDDto {
  userId: string;
  start: number;
  finish: number;
  state: CalendarActivityState;
  activity: ActivityDto;
  program: ProgramActivityDto;
  feedback: CalendarFeedbackEbd;
}

export interface ProgramDto extends IDDto {
  name: string;
  description: string;
  image: string;
  activities: ProgramActivityDto[];
}

export enum ProtocolType {
  ErrorMessage = 0,
  NewMessage = 1,
  ActivityFeedback = 2,
}

export interface ChatProtocolHead {
  type: ProtocolType;
  owner?: string;
  chatId: string;
}

export interface ChatProtocolBody {
  message: string;
  entry?: CalendarEntryDto;
  [key: string]: any;
}

export interface ChatProtocolTail {
  created?: number;
  updated?: number;
}

export interface ChatProtocol {
  head: ChatProtocolHead;
  body: ChatProtocolBody;
  tail: ChatProtocolTail;
}

export interface UserContactDto extends UUIDDto {
  userId: string;
  contactId: string;
  contact: UserDto;
}

export interface ConversationDto extends UUIDDto {
  users: UserDto[];
  lastMessageSentAt?: number;
  lastMessagePreview?: string;
  lastMessageUserId?: string;
  lastMessageUserName?: string;
  lastMessageSeen?: any;
}

export interface UpdatePatientDayProgram {
  day: number;
  level: number;
  type: string;
  start: number;
  finish: number;
  programId: number;
}

export enum NotificationProtocolType {
  ErrorMessage = 0,
  NewPatient = 1,
  PatientAssigned = 2,
  ActivityFeedback = 3,
  ProgramEndSoon = 4,
}

export interface NotificationProtocolHead {
  type: NotificationProtocolType;
}

export interface NotificationProtocolBody {
  name: string;
  description: string;
  [key: string]: any;
}

export interface NotificationProtocolTail {
  created?: number;
  updated?: number;
}

export interface NotificationProtocol {
  /** Stored notification id; absent on live socket notifications. */
  id?: string;
  head: NotificationProtocolHead;
  body: NotificationProtocolBody;
  tail: NotificationProtocolTail;
}

export interface UserNotificationDto extends UUIDDto {
  userId: string;
  type: NotificationProtocolType;
  name: string;
  description: string;
  code: string;
  params: object;
  isRead: boolean;
}

export interface SpecialistTodoDto {
  entryId: string;
  id: string;
  specialistId: string;
  created: number;
  updated: number;
  start: number;
  finish: number;
  email: string;
  profile: UserProfileDto;
  activity: ActivityDto;
}

export enum QuestionnaireType {
  Welcome = 'welcome',
  WellBeing = 'well-being',
  PhysicalFunction = 'physical-activity',
}

export interface HealthReportDto {
  id: string;
  userId: string;
  type: QuestionnaireType;
  value: number;
  createdAt: string;
  updatedAt: string;
}

export interface UserMessageDto extends UUIDDto {
  userId: string;
  chatId: string;
  type: ProtocolType;
  message: string;
  params: any;
  isRead: boolean;
  messageType: 'text' | 'file';
  /** Present on file messages; original upload name. */
  fileName?: string;
}

export interface ChatDto {
  id: string;
  users: UserDto[];
  lastMessage: UserMessageDto | null;
  unreadMessages: number;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduledMessageDto extends UUIDDto {
  chatId: string;
  userId: string;
  message: string;
  scheduledAt: number;
  status: 'pending' | 'sending' | 'sent' | 'cancelled' | 'failed';
  jobId: string | null;
  sentMessageId: string | null;
  failureReason: string | null;
}

export type ExerciseType = 'breathing' | 'mindfulness' | 'walking' | 'strength';

const levelRange: Record<ExerciseType, [number, number]> = {
  breathing: [1, 1],
  mindfulness: [1, 6],
  walking: [1, 10],
  strength: [1, 5],
};

export const getLevelRange = (exerciseType: ExerciseType): [number, number] => {
  return levelRange[exerciseType];
};
